import { Button } from "../../ui/button"
import { Input } from "../../ui/input"
import { Label } from "../../ui/label"
import { Alert, AlertDescription, AlertTitle } from "../../ui/alert"
import { AlertCircle, UserCircle, Home, Crown, CreditCard } from 'lucide-react'
import { useAccount } from '../hooks/Account'
import TopBar from '../../utils/TopBar'
import Footer from '../../utils/Footer'

export default function Account() {
  const {
    userData,
    isEditing,
    errorMessage,
    isFormChanged,
    handleSubmit,
    handleInputChange,
    handleCancel,
    setIsEditing,
    formatMembershipDuration
  } = useAccount()

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col relative">
      <TopBar 
        title="Account Settings"
        subtitle="Manage your personal information and preferences"
      />

      <main className="flex-1 overflow-x-hidden overflow-y-auto">
        <div className="max-w-4xl mx-auto py-6 sm:px-6 lg:px-8">
          {!isEditing && (
            <div className="mb-6">
              <Button 
                onClick={() => setIsEditing(true)}
                className="bg-blue-600/80 hover:bg-blue-700 text-white"
              >
                Edit Profile
              </Button>
            </div>
          )}

          {errorMessage && (
            <Alert variant="destructive" className="mb-4 bg-red-900/50 border-red-700">
              <AlertCircle className="h-4 w-4 text-red-400" />
              <AlertTitle className="text-red-400">Error</AlertTitle>
              <AlertDescription className="text-red-300">{errorMessage}</AlertDescription>
            </Alert>
          )}

          {userData && (
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Personal Information Card */}
              <div className="bg-black border border-gray-800 shadow-xl rounded-lg overflow-hidden">
                <div className="px-6 py-5">
                  <div className="flex items-center mb-4">
                    <UserCircle className="mr-2 text-blue-500" size={24} />
                    <h2 className="text-xl font-semibold text-white">Personal Information</h2>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="first_name" className="text-gray-300">First Name</Label>
                        <Input
                          id="first_name"
                          name="first_name"
                          value={userData.first_name}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          className="mt-1 bg-gray-900/50 border-gray-800 text-white focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <Label htmlFor="last_name" className="text-gray-300">Last Name</Label>
                        <Input
                          id="last_name"
                          name="last_name"
                          value={userData.last_name}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          className="mt-1 bg-gray-900/50 border-gray-800 text-white focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="email" className="text-gray-300">Email</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={userData.email}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          className="mt-1 bg-gray-900/50 border-gray-800 text-white focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="text-gray-300">Phone</Label>
                        <Input
                          id="phone"
                          name="phone"
                          value={userData.phone}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          className="mt-1 bg-gray-900/50 border-gray-800 text-white focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="gender" className="text-gray-300">Gender</Label>
                        <select
                          id="gender"
                          name="gender"
                          value={userData.gender}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          className="mt-1 block w-full rounded-md border-gray-800 bg-gray-900/50 text-white focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="M">Male</option>
                          <option value="F">Female</option>
                          <option value="N">Prefer not to identify</option>
                        </select>
                      </div>
                      <div>
                        <Label htmlFor="birth_date" className="text-gray-300">Birth Date</Label>
                        <Input
                          id="birth_date"
                          name="birth_date"
                          type="date"
                          value={userData.birth_date}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          className="mt-1 bg-gray-900/50 border-gray-800 text-white focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Address Information Card */}
              <div className="bg-black border border-gray-800 shadow-xl rounded-lg overflow-hidden">
                <div className="px-6 py-5">
                  <div className="flex items-center mb-4">
                    <Home className="mr-2 text-blue-500" size={24} />
                    <h2 className="text-xl font-semibold text-white">Address Information</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="street" className="text-gray-300">Street</Label>
                        <Input
                          id="street"
                          name="street"
                          value={userData.street}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          className="mt-1 bg-gray-900/50 border-gray-800 text-white focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <Label htmlFor="house_number" className="text-gray-300">Number</Label>
                        <Input
                          id="house_number"
                          name="house_number"
                          value={userData.house_number}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          className="mt-1 bg-gray-900/50 border-gray-800 text-white focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="complement" className="text-gray-300">Complement</Label>
                      <Input
                        id="complement"
                        name="complement"
                        value={userData.complement || ''}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="mt-1 bg-gray-900/50 border-gray-800 text-white focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="city" className="text-gray-300">City</Label>
                        <Input
                          id="city"
                          name="city"
                          value={userData.city}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          className="mt-1 bg-gray-900/50 border-gray-800 text-white focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <Label htmlFor="state" className="text-gray-300">State</Label>
                        <Input
                          id="state"
                          name="state"
                          value={userData.state}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          className="mt-1 bg-gray-900/50 border-gray-800 text-white focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="country" className="text-gray-300">Country</Label>
                        <Input
                          id="country"
                          name="country"
                          value={userData.country}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          className="mt-1 bg-gray-900/50 border-gray-800 text-white focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <Label htmlFor="zip_code" className="text-gray-300">ZIP Code</Label>
                        <Input
                          id="zip_code"
                          name="zip_code"
                          value={userData.zip_code}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          className="mt-1 bg-gray-900/50 border-gray-800 text-white focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Membership Information Card */}
              <div className="bg-black border border-gray-800 shadow-xl rounded-lg overflow-hidden">
                <div className="px-6 py-5">
                  <div className="flex items-center mb-4">
                    <Crown className="mr-2 text-blue-500" size={24} />
                    <h2 className="text-xl font-semibold text-white">Membership Information</h2>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <span className="text-gray-300">Membership Type:</span>
                      <span className="ml-2 px-2 py-1 bg-blue-500/20 text-blue-400 rounded-md text-sm">
                        {userData.membership_type === 'premium' ? 'Premium' : 'Free'}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-gray-300">Member Since:</span>
                      <span className="ml-2 text-gray-300">
                        {formatMembershipDuration(userData.date_joined)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Information Card */}
              <div className="bg-black border border-gray-800 shadow-xl rounded-lg overflow-hidden">
                <div className="px-6 py-5">
                  <div className="flex items-center mb-4">
                    <CreditCard className="mr-2 text-blue-500" size={24} />
                    <h2 className="text-xl font-semibold text-white">Payment Information</h2>
                  </div>
                  <div className="space-y-3">
                    <p className="text-gray-400 italic">Payment information will be available soon.</p>
                  </div>
                </div>
              </div>

              {/* Action Buttons - now only show Save/Cancel when editing */}
              {isEditing && (
                <div className="flex justify-end space-x-4 py-4">
                  <Button 
                    onClick={handleCancel}
                    className="bg-red-600/80 hover:bg-red-700 text-white"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    onClick={handleSubmit}
                    className={`${
                      isFormChanged 
                        ? 'bg-green-600/80 hover:bg-green-700' 
                        : 'bg-gray-600/80 hover:bg-gray-700'
                    } text-white`}
                    disabled={!isFormChanged}
                  >
                    Save Changes
                  </Button>
                </div>
              )}
            </form>
          )}
        </div>
      </main>

      <Footer />
    </div>
  )
}

